import { type AutonumberTemplate, autonumberValue } from '../autonumber.ts';
import type { ListFieldType } from '../field-types.ts';
import { CURRENCY_CODE, moneyLiteral } from '../money.ts';
import {
  type DayRange,
  dayOfToken,
  dayRangeBounds,
  type FilterClock,
  isMomentToken,
  rangeOfToken,
} from './dates.ts';
import {
  type BaseFieldType,
  EMPTY_INCLUSIVE_OPERATORS,
  type FilterCondition,
  type FilterItem,
  type FilterNode,
  type FilterToken,
  groupOf,
  INACTIVE_USERS_TOKEN,
  isFilterGroup,
  isFilterRelated,
  isFilterToken,
  isRecordToken,
  NO_VALUE_OPERATORS,
  OWNER_FIELD,
  OWNER_UNIT_FIELD,
  UNIT_TOKENS,
  USER_TOKENS,
  valuesOf,
} from './tree.ts';

/**
 * Оцінювач мови умов (filter-language.md, розд. 8): те саме дерево над значеннями одного запису,
 * без запиту до сервера. Ним користуються видимість у макеті й попередня перевірка умов у
 * браузері; рішення завжди за сервером. Семантика збігається з компілятором DAL — це тримає тест
 * властивостей «SQL = оцінювач» на справжньому PostgreSQL (`packages/data`, FLT-20, критерій 2):
 * - порожнє (`null`) не проходить жодної умови, крім `is_null` і операторів, що включають порожні
 *   (`neq`, `not_in`, `has_none`; `excludeEmpty: true` — без порожніх) (deny by default);
 * - `starts_with` — без регістру й варіантів апострофа (`crm.norm`), `%` і `_` — буквально;
 * - числа — десяткові без втрати точності, моменти — до мікросекунди, `uuid` — без регістру;
 * - дні й діапазони — у поясі годинника (`FilterClock`), межі дня — з `dates.ts`, як у DAL;
 * - поле-список (Б3) — «хоч одне значення» (`neq` / `not_in` — «жодного»), значення умови
 *   нормалізує `env.lists.normalize` (та сама функція, що в DAL);
 * - профіль `requirement` (field-rules.md, розд. 2): крок через зв'язок — значення поля батька за
 *   ключем `<lookup>.<поле>` у записі (його кладе викликач: батько живий і видимий — значення, інакше
 *   `null`), тип — `env.typeOf` того самого ключа; `related … exists` — `env.related`; токен
 *   `record` — значення поля `env.tokenRecord` того самого типу: порожнє не дорівнює нічому, тож
 *   `eq` хибне, `neq` істинне, порядок (`lt`…) хибний.
 *
 * Дерево оцінювач не перевіряє: недопустима умова (оператор не для типу, значення не того типу,
 * токен поза полем) дає `false`, як і невідоме поле. Перевіряє дерево `parseFilterTree` і сервер.
 */

/** Перехідний стан переведень однієї гілки підрозділу глядача (Д1, розд. 7). */
export interface FilterOwnerMoves {
  readonly movedOut: readonly string[];
  readonly movedIn: readonly string[];
}

/** Хто дивиться: з цього сервер і оцінювач обчислюють токени людини (розд. 3). */
export interface FilterViewer {
  /** Людина; `null` — нелюдський принципал: токени людини не дають нікого. */
  readonly userId: string | null;
  /** Учасники моїх команд (зі мною чи без — «я» додається завжди). */
  readonly teamMemberIds: readonly string[];
  readonly unitIds: readonly string[];
  /** Мої підрозділи з нащадками. */
  readonly unitTreeIds: readonly string[];
  readonly ownerMoves?:
    | { readonly unit: FilterOwnerMoves; readonly unitTree: FilterOwnerMoves }
    | null
    | undefined;
}

/**
 * Поля-списки для оцінювача: тип списку поля і нормалізація значення умови. Нормалізатор —
 * `normalizeValue` з `@clientline/contracts/values` (з країною телефону компанії); він тягне
 * метадані телефонів, тож чанк картки підвантажує його ліниво. Без нього умова зі значенням на
 * полі-списку хибна, а `is_null` / `not_null` рахуються.
 */
export interface FilterListEnv {
  kindOf(field: string): ListFieldType | undefined;
  /** Канонічне значення або `undefined` — значення недопустиме. */
  normalize?(kind: ListFieldType, raw: unknown): string | undefined;
}

/**
 * Автономер для оцінювача (FLD-22): поточний і попередні шаблони поля з `/meta`. Запис несе рядок
 * номера (`SO-000124`), умова порівнює число: без шаблонів рядок не розбирається, і умова зі
 * значенням хибна (`is_null` / `not_null` рахуються).
 */
export interface FilterAutonumberEnv {
  templatesOf(field: string): readonly AutonumberTemplate[] | undefined;
}

export interface FilterEvalEnv {
  /** Базовий тип поля; `undefined` — поля немає (умова хибна). */
  typeOf(field: string): BaseFieldType | undefined;
  viewer: FilterViewer;
  /** Годинник днів і моменту; без нього умова з днем у поясі чи токеном дат хибна. */
  clock?: FilterClock | undefined;
  /** Деактивовані люди IAM (`inactive_users`); без переліку токен нікого не дає. */
  inactiveUserIds?: readonly string[] | undefined;
  lists?: FilterListEnv | undefined;
  autonumbers?: FilterAutonumberEnv | undefined;
  /**
   * Запис, що пишеться, для токена `record` (Б4): у профілі `requirement` — сам оцінюваний запис
   * (`evaluateRequirement`), у фільтрі вибору — значення форми. Немає — умова з токеном хибна.
   * Ключі — як у записі: власні поля й кроки `<lookup>.<поле>`.
   */
  tokenRecord?: FilterRecord | undefined;
  /**
   * Чи є живі дочірні за вхідним зв'язком `<сутність>.<lookup>` (`related … exists`, FLT-16).
   * `undefined` — невідомо: умова хибна в обидва боки (deny by default).
   */
  related?: ((key: string) => boolean | undefined) | undefined;
}

/** Запис: значення за ключами полів, як їх віддає API (`owner`, `owner_unit` — системні). */
export type FilterRecord = Readonly<Record<string, unknown>>;

/** Дерево над записом: група `and` — усі, `or` — хоч одна, умова — за оператором. */
export function evaluateFilter(
  node: FilterNode,
  record: FilterRecord,
  env: FilterEvalEnv,
): boolean {
  if (isFilterGroup(node)) {
    const { kind, items } = groupOf(node);
    return kind === 'and'
      ? items.every((item) => evaluateFilter(item, record, env))
      : items.some((item) => evaluateFilter(item, record, env));
  }
  if (isFilterRelated(node)) {
    const exists = env.related?.(node.related);
    return exists !== undefined && exists === node.exists;
  }
  return evaluateCondition(node, record, env);
}

/**
 * Умова профілю `requirement` над записом, що пишеться (умова входу в етап, правило між полями):
 * токен `record` бере значення того самого запису. Кроки й `related` — як в `evaluateFilter`.
 * Автономера профіль не бере (filter-language.md, розд. 5): умова на ньому, зокрема через крок чи
 * токеном, хибна, як на невідомому полі (DAL відмовляє — `filter_operator_not_allowed`).
 */
export function evaluateRequirement(
  node: FilterNode,
  record: FilterRecord,
  env: Omit<FilterEvalEnv, 'tokenRecord'>,
): boolean {
  const typeOf = (field: string) => {
    const type = env.typeOf(field);
    return type === 'autonumber' ? undefined : type;
  };
  return evaluateFilter(node, record, { ...env, typeOf, tokenRecord: record });
}

const DECIMAL = /^-?\d{1,15}(\.\d{1,6})?$/;
const NUMBER_TEXT = /^([+-]?)(\d+)(?:\.(\d*))?(?:e([+-]?\d+))?$/i;
/** Рік 0000 PostgreSQL не приймає (`date/time field value out of range`): з 0001. */
const ISO_DATE = /^(?!0000)\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Момент з поясом: ISO 8601 (`T`) або текст PostgreSQL (пробіл), до 6 знаків дробу. */
const MOMENT =
  /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?(?:(Z)|([+-])(\d{2})(?::?(\d{2}))?)$/;

/** Нормалізація тексту для `starts_with` — як `crm.norm` (міграція 0008): апострофи, нижній регістр. */
export function normText(value: string): string {
  return value.replace(/[’ʼ‘]/g, "'").toLocaleLowerCase('uk-UA');
}

/** Дата `YYYY-MM-DD`, що існує в календарі (рік від 0001). */
export function isCalendarDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const time = Date.parse(`${value}T00:00:00Z`);
  return !Number.isNaN(time) && new Date(time).toISOString().slice(0, 10) === value;
}

/**
 * Момент у мікросекундах від епохи (JS-число точне до ~2255 року). Рядок — лише з поясом
 * (`Z` чи `±HH[:MM]`), бо без поясу PostgreSQL узяв би пояс сеансу. Зсув — до ±15:59, як приймає
 * PostgreSQL (інакше 22009). `Date` — з мілісекундами.
 */
export function momentMicros(value: unknown): number | undefined {
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? undefined : time * 1000;
  }
  if (typeof value !== 'string' || value.length > 40) return undefined;
  const m = MOMENT.exec(value);
  if (!m) return undefined;
  const [, day = '', hh, mm, ss = '00', fraction = '', utc, sign, zh = '00', zm = '00'] = m;
  if (!isCalendarDate(day)) return undefined;
  if (utc === undefined && (Number(zh) > 15 || Number(zm) > 59)) return undefined;
  const offset = utc === undefined ? `${sign}${zh}:${zm}` : 'Z';
  const base = Date.parse(`${day}T${hh}:${mm}:${ss}${offset}`);
  if (Number.isNaN(base)) return undefined;
  return base * 1000 + Number(fraction.padEnd(6, '0'));
}

/** Десяткове число як знак і цифри без втрати точності (рядок, JS-число, експонента). */
interface Decimal {
  negative: boolean;
  /** Цифри без ведучих нулів у цілій частині й без кінцевих у дробовій. */
  int: string;
  frac: string;
}

function decimalOf(value: unknown): Decimal | undefined {
  let text: string;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return undefined;
    text = String(value);
  } else if (typeof value === 'string') {
    text = value.trim();
  } else return undefined;
  const m = NUMBER_TEXT.exec(text);
  if (!m) return undefined;
  const [, sign = '', intPart = '', fracPart = '', exp = '0'] = m;
  let digits = `${intPart}${fracPart}`;
  let point = intPart.length + Number(exp);
  if (point < 0) {
    digits = `${'0'.repeat(-point)}${digits}`;
    point = 0;
  }
  if (point > digits.length) digits = digits.padEnd(point, '0');
  const int = digits.slice(0, point).replace(/^0+/, '');
  const frac = digits.slice(point).replace(/0+$/, '');
  const zero = int === '' && frac === '';
  return { negative: sign === '-' && !zero, int, frac };
}

function compareMagnitude(a: Decimal, b: Decimal): number {
  if (a.int.length !== b.int.length) return a.int.length < b.int.length ? -1 : 1;
  if (a.int !== b.int) return a.int < b.int ? -1 : 1;
  const width = Math.max(a.frac.length, b.frac.length);
  const fa = a.frac.padEnd(width, '0');
  const fb = b.frac.padEnd(width, '0');
  return fa === fb ? 0 : fa < fb ? -1 : 1;
}

function compareDecimal(a: Decimal, b: Decimal): number {
  if (a.negative !== b.negative) return a.negative ? -1 : 1;
  const magnitude = compareMagnitude(a, b);
  return a.negative ? -magnitude : magnitude;
}

/**
 * Значення умови для базового типу поля — одне правило для параметра SQL (`coerceValue` у DAL) і
 * літерала оцінювача: текст і ключ — до 200 символів; число — до 15 цифр і 6 знаків після коми,
 * без експоненти; дата — календарна, з 0001; момент — ISO чи текст PostgreSQL лише з поясом (до
 * ±15:59); `uuid` — без регістру; логічне. `undefined` — недопустиме (`filter_value_invalid`).
 */
export function filterLiteral(type: BaseFieldType, value: unknown): string | boolean | undefined {
  switch (type) {
    case 'text':
    case 'picklist':
      return typeof value === 'string' && value.length <= 200 ? value : undefined;
    case 'number': {
      const text = typeof value === 'number' && Number.isFinite(value) ? String(value) : value;
      return typeof text === 'string' && DECIMAL.test(text) ? text : undefined;
    }
    case 'date':
      return typeof value === 'string' && isCalendarDate(value) ? value : undefined;
    case 'datetime':
      return typeof value === 'string' && momentMicros(value) !== undefined ? value : undefined;
    case 'uuid':
      return typeof value === 'string' && UUID.test(value) ? value.toLowerCase() : undefined;
    case 'boolean':
      return typeof value === 'boolean' ? value : undefined;
    // Множина не порівнюється скаляром: лише `has_*` і порожнє.
    case 'multipicklist':
      return undefined;
    // Автономер: ціле 1…10^15 − 1 числом чи рядком цифр. Рядок за шаблоном поля перекладає в число
    // той, хто знає шаблони (`coerceValue` DAL, `autonumberCondition` оцінювача), до цього кроку.
    case 'autonumber': {
      const n = autonumberValue([], value);
      return n === null ? undefined : String(n);
    }
    // Гроші: скалярний літерал — число, що порівнює суму в усіх валютах (значення-гроші розбирає
    // `moneyItem`, бо воно не скаляр).
    case 'money':
      return filterLiteral('number', value);
  }
}

/**
 * Елемент умови над грошима (money-type.md, розд. 3): значення-гроші `{ amount, currency }` —
 * сума в межах валюти; число — сума в усіх валютах. Одне правило для DAL і оцінювача;
 * `undefined` — значення недопустиме (`filter_value_invalid`).
 */
export function moneyItem(value: unknown): { amount: string; currency: string | null } | undefined {
  const money = moneyLiteral(value);
  if (money) return money;
  const amount = filterLiteral('number', value);
  return typeof amount === 'string' ? { amount, currency: null } : undefined;
}

/**
 * Коди валют `currency_in`: 1…100 кодів форми ISO 4217 без повторів. Довідником не обмежено: валюта,
 * вилучена з довідника, лишається в старих значеннях, і їх треба знаходити.
 */
export function currencyCodes(value: unknown): string[] | undefined {
  if (!Array.isArray(value) || value.length === 0 || value.length > 100) return undefined;
  const raw: unknown[] = value;
  if (!raw.every((code) => typeof code === 'string' && CURRENCY_CODE.test(code))) return undefined;
  return [...new Set(raw as string[])];
}

/** Літерал умови в порівнюваній формі: число — десяткове, момент — мікросекунди. */
function coerceLiteral(type: BaseFieldType, value: unknown): unknown {
  const literal = filterLiteral(type, value);
  if (literal === undefined) return undefined;
  if (type === 'number') return decimalOf(literal);
  if (type === 'datetime') return momentMicros(literal);
  return literal;
}

/** Значення запису за типом поля; порожнє — `null`. */
function storedOf(type: BaseFieldType, value: unknown): unknown {
  if (value === null || value === undefined) return null;
  switch (type) {
    case 'number':
      return decimalOf(value) ?? null;
    case 'datetime':
      return momentMicros(value) ?? null;
    case 'date':
      return value instanceof Date ? value.toISOString().slice(0, 10) : value;
    case 'uuid':
      return typeof value === 'string' ? value.toLowerCase() : value;
    default:
      return value;
  }
}

function sameValue(type: BaseFieldType, stored: unknown, literal: unknown): boolean {
  if (type === 'number') return compareDecimal(stored as Decimal, literal as Decimal) === 0;
  return stored === literal;
}

function order(type: BaseFieldType, stored: unknown, literal: unknown): number | undefined {
  switch (type) {
    case 'number':
      return compareDecimal(stored as Decimal, literal as Decimal);
    case 'datetime':
      return (stored as number) - (literal as number);
    case 'date': {
      const a = stored as string;
      const b = literal as string;
      return a === b ? 0 : a < b ? -1 : 1;
    }
    default:
      return undefined;
  }
}

/** Люди за токеном людини (без підрозділу): `me` — я, `my_teams` — я й учасники моїх команд. */
function tokenPeople(token: string, viewer: FilterViewer): readonly string[] | undefined {
  const human = viewer.userId?.toLowerCase() ?? null;
  if (token === 'me') return human ? [human] : [];
  if (token === 'my_teams') {
    return human ? [human, ...viewer.teamMemberIds.map((id) => id.toLowerCase())] : [];
  }
  return undefined;
}

/**
 * `owner` у підрозділі глядача за токеном (Д1, розд. 7): штамп `owner_unit` серед моїх
 * підрозділів, а людина не переведена геть, або людину переведено до мене (ще не перештамповано).
 * Запис без власника (assignment.md, розд. 3) — за штампом: нікуди не переведений, як `is null or`
 * у SQL (`ownerNotMovedOut`, packages/data).
 */
function ownerInUnit(token: string, record: FilterRecord, viewer: FilterViewer): boolean {
  if (viewer.userId === null) return false;
  const tree = token === 'my_unit_tree';
  const units = tree ? viewer.unitTreeIds : viewer.unitIds;
  const moves = tree ? viewer.ownerMoves?.unitTree : viewer.ownerMoves?.unit;
  const owner = typeof record[OWNER_FIELD] === 'string' ? (record[OWNER_FIELD] as string) : null;
  const unit =
    typeof record[OWNER_UNIT_FIELD] === 'string' ? (record[OWNER_UNIT_FIELD] as string) : null;
  const lower = owner?.toLowerCase() ?? null;
  if (lower !== null && (moves?.movedIn ?? []).some((id) => id.toLowerCase() === lower)) {
    return true;
  }
  if (unit === null || !units.some((id) => id.toLowerCase() === unit.toLowerCase())) return false;
  return lower === null || !(moves?.movedOut ?? []).some((id) => id.toLowerCase() === lower);
}

/** Збіг значення запису з одним елементом умови `eq` / `in` / `not_in` (літерал чи токен). */
function matchesItem(
  type: BaseFieldType,
  condition: FilterCondition,
  stored: unknown,
  item: FilterItem,
  record: FilterRecord,
  env: FilterEvalEnv,
): boolean {
  if (isFilterToken(item)) {
    if (type !== 'uuid' || item.n !== undefined) return false;
    if (UNIT_TOKENS.has(item.token)) {
      return condition.field === OWNER_FIELD && ownerInUnit(item.token, record, env.viewer);
    }
    if (item.token === INACTIVE_USERS_TOKEN) {
      return (env.inactiveUserIds ?? []).some((id) => id.toLowerCase() === stored);
    }
    // Невідомий токен — нікого.
    return (tokenPeople(item.token, env.viewer) ?? []).includes(stored as string);
  }
  const literal = coerceLiteral(type, item);
  return literal !== undefined && sameValue(type, stored, literal);
}

const MEMBER_TOKENS: ReadonlySet<string> = new Set([...USER_TOKENS, INACTIVE_USERS_TOKEN]);

/** Токен на місці елемента `eq` / `in` / `not_in`: людини, підрозділу чи «неактивні». */
function memberToken(item: FilterToken): boolean {
  return item.n === undefined && MEMBER_TOKENS.has(item.token);
}

/** Елементи `eq` / `in` / `not_in` допустимі: токени членства або літерали типу поля, 1…100. */
function validItems(type: BaseFieldType, items: readonly FilterItem[]): boolean {
  if (items.length === 0 || items.length > 100) return false;
  return items.every((item) =>
    isFilterToken(item)
      ? type === 'uuid' && memberToken(item)
      : filterLiteral(type, item) !== undefined,
  );
}

/** День умови `on` / `before` / `after`: дата `YYYY-MM-DD` або токен дня. */
function conditionDay(value: unknown, clock: FilterClock | undefined): string | undefined {
  if (typeof value === 'string') return isCalendarDate(value) ? value : undefined;
  if (isFilterToken(value) && clock) return dayOfToken(value, clock);
  return undefined;
}

/** Умова дня й діапазону днів над датою (рядок) чи моментом (мікросекунди). */
function dayCondition(
  type: BaseFieldType,
  op: string,
  stored: unknown,
  value: unknown,
  clock: FilterClock | undefined,
): boolean {
  if (type !== 'date' && type !== 'datetime') return false;
  let range: DayRange | undefined;
  if (op === 'within') {
    range = isFilterToken(value) && clock ? rangeOfToken(value, clock) : undefined;
  } else {
    const day = conditionDay(value, clock);
    range = day === undefined ? undefined : { from: day, to: day };
  }
  if (range === undefined || stored === null) return false;
  if (type === 'date') {
    const d = stored as string;
    if (op === 'before') return d < range.from;
    if (op === 'after') return d > range.to;
    return d >= range.from && d <= range.to;
  }
  // Момент: межі дня — у поясі годинника (без годинника — хибно, як і токен).
  if (!clock) return false;
  const at = stored as number;
  // Межі — мікросекунди; на краях календаря ±Infinity, як `±infinity` у DAL.
  const bounds = dayRangeBounds(range, clock.timeZone);
  const from = bounds.start * 1000;
  const to = bounds.end * 1000;
  if (op === 'before') return at < from;
  if (op === 'after') return at >= to;
  return at >= from && at < to;
}

/** Роздільники номера в префіксі (як у нормалізаторі телефону): пробіли, дужки, дефіси, крапки. */
const PHONE_PREFIX_SEPARATORS = /[\s().\-/\u2010-\u2015\u2212]/gu;

/**
 * Канонічний префікс `starts_with` на полі-списку (uniqueness.md, розд. 7): телефон — префікс цифр
 * E.164 (`+380 67` → `+38067`, 1…15 цифр, лише цифри й роздільники); пошта — NFKC і нижній регістр,
 * як нормалізатор; ідентифікатор — NFC з регістром. `undefined` — префікс недопустимий.
 */
export function listPrefix(kind: ListFieldType, prefix: unknown): string | undefined {
  if (typeof prefix !== 'string' || prefix.length === 0 || prefix.length > 200) return undefined;
  if (kind === 'phone') {
    const digits = prefix.normalize('NFKC').trim().replace(PHONE_PREFIX_SEPARATORS, '');
    const bare = digits.startsWith('+') ? digits.slice(1) : digits;
    return /^\d{1,15}$/.test(bare) ? `+${bare}` : undefined;
  }
  const value = kind === 'email' ? prefix.normalize('NFKC').toLowerCase() : prefix.normalize('NFC');
  return value.length > 0 && value.length <= 200 ? value : undefined;
}

/** Значення поля-списку запису: елементи `{ value }` (API) або рядки. */
function listValues(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item === 'string') out.push(item);
    else if (typeof item === 'object' && item !== null && typeof item.value === 'string') {
      out.push(item.value);
    }
  }
  return out;
}

/** Умова над полем-списком (Б3): «хоч одне значення»; `neq` / `not_in` — «жодного». */
function listCondition(
  kind: ListFieldType,
  condition: FilterCondition,
  raw: unknown,
  env: FilterEvalEnv,
): boolean {
  const values = listValues(raw);
  const { op } = condition;
  if (NO_VALUE_OPERATORS.has(op)) {
    if (condition.value !== undefined) return false;
    return op === 'is_null' ? values.length === 0 : values.length > 0;
  }
  if (op === 'starts_with') {
    const prefix = listPrefix(kind, condition.value);
    return prefix !== undefined && values.some((value) => value.startsWith(prefix));
  }
  if (op !== 'eq' && op !== 'in' && op !== 'neq' && op !== 'not_in') return false;
  const array = op === 'in' || op === 'not_in';
  if (array !== Array.isArray(condition.value)) return false;
  const items = valuesOf(condition);
  if (items.length === 0 || items.length > 100 || !env.lists?.normalize) return false;
  const wanted = new Set<string>();
  for (const item of items) {
    const value = isFilterToken(item) ? undefined : env.lists.normalize(kind, item);
    if (value === undefined) return false;
    wanted.add(value);
  }
  const any = values.some((value) => wanted.has(value));
  if (op === 'eq' || op === 'in') return any;
  return !any && (condition.excludeEmpty !== true || values.length > 0);
}

/** Оператори з токеном `record` (Б4): рівність і порядок одного значення з одним. */
const RECORD_OPERATORS: ReadonlySet<string> = new Set(['eq', 'neq', 'lt', 'lte', 'gt', 'gte']);

/**
 * Умова з токеном `record`: поле умови проти поля запису, що пишеться (`env.tokenRecord`), того
 * самого базового типу (інакше хибна, DAL — `filter_value_invalid`). Порожнє не дорівнює нічому:
 * `eq` — лише обидва задані й однакові; `neq` — заперечення `eq` (порожнє поле умови з
 * `excludeEmpty` — хибно); порядок — лише обидва задані. Як `=`, `<>` і `<` у SQL з `coalesce`.
 */
function recordCondition(
  type: BaseFieldType,
  condition: FilterCondition,
  stored: unknown,
  field: string,
  env: FilterEvalEnv,
): boolean {
  const { op } = condition;
  if (!RECORD_OPERATORS.has(op) || type === 'multipicklist' || !env.tokenRecord) return false;
  if (env.typeOf(field) !== type || env.lists?.kindOf(field) !== undefined) return false;
  const other = storedOf(type, env.tokenRecord[field]);
  const both = stored !== null && other !== null;
  if (op === 'eq') return both && sameValue(type, stored, other);
  if (op === 'neq') {
    if (stored === null) return condition.excludeEmpty !== true;
    return !(both && sameValue(type, stored, other));
  }
  if (!both) return false;
  const cmp = order(type, stored, other);
  if (cmp === undefined) return false;
  if (op === 'lt') return cmp < 0;
  if (op === 'lte') return cmp <= 0;
  if (op === 'gt') return cmp > 0;
  return cmp >= 0;
}

/**
 * Умова над автономером (FLD-22): рядок запису й значення умови — у число за шаблонами поля, далі —
 * як умова над числом. Рядок запису, якого шаблони не розбирають, непорожній, але з числом не
 * порівнюється (хибно все, крім `is_null` / `not_null`).
 */
function autonumberCondition(
  condition: FilterCondition,
  raw: unknown,
  env: FilterEvalEnv,
): boolean {
  const templates = env.autonumbers?.templatesOf(condition.field) ?? [];
  const empty = raw === null || raw === undefined || raw === '';
  const stored = empty ? null : autonumberValue(templates, raw);
  const numeric = (value: unknown): number | null =>
    isFilterToken(value) ? null : autonumberValue(templates, value);
  if (condition.value !== undefined) {
    const items = Array.isArray(condition.value) ? condition.value : [condition.value];
    const values = items.map(numeric);
    if (values.some((value) => value === null)) return false;
    if (!empty && stored === null) return false;
    const value = Array.isArray(condition.value) ? values : values[0];
    return evaluateCondition(
      { ...condition, value: value as NonNullable<FilterCondition['value']> },
      { [condition.field]: stored },
      { ...env, typeOf: () => 'number', lists: undefined },
    );
  }
  return evaluateCondition(
    condition,
    { [condition.field]: empty ? null : 0 },
    { ...env, typeOf: () => 'number', lists: undefined },
  );
}

/** Збережене значення-гроші запису (API): `{ amount, currency }`; інше — порожнє. */
function storedMoneyOf(raw: unknown): { amount: Decimal; currency: string } | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const { amount, currency } = raw as Record<string, unknown>;
  const decimal = decimalOf(amount);
  return decimal && typeof currency === 'string' ? { amount: decimal, currency } : null;
}

/** Порівняння суми з елементом умови: валюта (якщо є) збігається, далі — порядок сум. */
function moneyCompare(
  stored: { amount: Decimal; currency: string },
  item: { amount: string; currency: string | null },
): number | undefined {
  if (item.currency !== null && item.currency !== stored.currency) return undefined;
  return compareDecimal(stored.amount, decimalOf(item.amount) as Decimal);
}

/**
 * Умова над грошима (money-type.md, розд. 3; filter-language.md, розд. 2): оператори числа над
 * сумою, значення-гроші — у межах валюти (інша валюта не дорівнює й не впорядковується), число — у
 * всіх валютах; `between` — дві межі одного виду (гроші — однієї валюти); `currency_in` — за
 * валютою. Порожнє — як у числа. Токен `record` — окремо, `moneyRecordCondition` (DAL — `filter_value_invalid`).
 */
function moneyCondition(condition: FilterCondition, raw: unknown): boolean {
  const stored = storedMoneyOf(raw);
  const { op, value } = condition;
  const exclude = condition.excludeEmpty === true;
  if (NO_VALUE_OPERATORS.has(op)) {
    if (value !== undefined) return false;
    return op === 'is_null' ? stored === null : stored !== null;
  }
  if (op === 'currency_in') {
    const codes = currencyCodes(value);
    return codes !== undefined && stored !== null && codes.includes(stored.currency);
  }
  const array = op === 'in' || op === 'not_in' || op === 'between';
  if (array !== Array.isArray(value)) return false;
  const items = valuesOf(condition).map(moneyItem);
  if (items.length === 0 || items.length > 100) return false;
  if (items.some((item) => item === undefined)) return false;
  const parsed = items as { amount: string; currency: string | null }[];
  const equal = (item: { amount: string; currency: string | null }) =>
    stored !== null && moneyCompare(stored, item) === 0;
  switch (op) {
    case 'eq':
      return equal(parsed[0] as { amount: string; currency: string | null });
    case 'in':
      return parsed.some(equal);
    case 'neq':
    case 'not_in':
      if (stored === null) return !exclude;
      return !parsed.some(equal);
    case 'lt':
    case 'lte':
    case 'gt':
    case 'gte': {
      if (stored === null) return false;
      const cmp = moneyCompare(stored, parsed[0] as { amount: string; currency: string | null });
      if (cmp === undefined) return false;
      if (op === 'lt') return cmp < 0;
      if (op === 'lte') return cmp <= 0;
      if (op === 'gt') return cmp > 0;
      return cmp >= 0;
    }
    case 'between': {
      const [from, to] = parsed;
      if (parsed.length !== 2 || !from || !to || from.currency !== to.currency) return false;
      if (stored === null) return false;
      const low = moneyCompare(stored, from);
      const high = moneyCompare(stored, to);
      return low !== undefined && high !== undefined && low >= 0 && high <= 0;
    }
    default:
      return false;
  }
}

/**
 * Гроші проти власного грошового поля того самого запису (`requirement`, #1331): лише `eq` і `neq`.
 * Сум у різних валютах не впорядкувати, тому порядку немає, а різна валюта — не рівність.
 * Порожнє — як у `recordCondition`.
 */
function moneyRecordCondition(
  condition: FilterCondition,
  raw: unknown,
  env: FilterEvalEnv,
): boolean {
  const { op, value } = condition;
  if ((op !== 'eq' && op !== 'neq') || !isRecordToken(value) || !env.tokenRecord) return false;
  if (value.field.includes('.') || env.typeOf(value.field) !== 'money') return false;
  const stored = storedMoneyOf(raw);
  const other = storedMoneyOf(env.tokenRecord[value.field]);
  const same =
    stored !== null &&
    other !== null &&
    stored.currency === other.currency &&
    compareDecimal(stored.amount, other.amount) === 0;
  if (op === 'eq') return same;
  if (stored === null) return condition.excludeEmpty !== true;
  return !same;
}

function setOf(value: unknown): readonly unknown[] | null {
  return Array.isArray(value) && value.length > 0 ? value : null;
}

function evaluateCondition(
  condition: FilterCondition,
  record: FilterRecord,
  env: FilterEvalEnv,
): boolean {
  const type = env.typeOf(condition.field);
  if (type === undefined) return false;
  const { op } = condition;
  const exclude = condition.excludeEmpty === true;
  if (exclude && !EMPTY_INCLUSIVE_OPERATORS.has(op)) return false;
  const raw = record[condition.field];
  const listKind = env.lists?.kindOf(condition.field);
  if (listKind !== undefined) return listCondition(listKind, condition, raw, env);
  if (type === 'autonumber') return autonumberCondition(condition, raw, env);
  if (type === 'money') {
    if (isRecordToken(condition.value)) return moneyRecordCondition(condition, raw, env);
    if (valuesOf(condition).some(isFilterToken)) return false;
    return moneyCondition(condition, raw);
  }
  if (type === 'multipicklist') {
    const set = setOf(raw);
    const keys = valuesOf(condition);
    const valid =
      keys.length > 0 &&
      keys.every((key) => typeof key === 'string') &&
      Array.isArray(condition.value);
    switch (op) {
      case 'is_null':
        return condition.value === undefined && set === null;
      case 'not_null':
        return condition.value === undefined && set !== null;
      case 'has_any':
        return valid && set !== null && keys.some((key) => set.includes(key));
      case 'has_all':
        return valid && set !== null && keys.every((key) => set.includes(key));
      case 'has_none':
        if (!valid) return false;
        if (set === null) return !exclude;
        return !keys.some((key) => set.includes(key));
      default:
        return false;
    }
  }
  const stored = storedOf(type, raw);
  if (isRecordToken(condition.value)) {
    return recordCondition(type, condition, stored, condition.value.field, env);
  }
  switch (op) {
    case 'is_null':
      return condition.value === undefined && stored === null;
    case 'not_null':
      return condition.value === undefined && stored !== null;
    case 'eq':
    case 'in':
    case 'not_in': {
      const array = op !== 'eq';
      if (array !== Array.isArray(condition.value)) return false;
      const items = valuesOf(condition);
      if (!validItems(type, items)) return false;
      if (stored === null) {
        if (op === 'not_in') return !exclude;
        // Запис без власника (assignment.md, розд. 3): токен підрозділу на `owner` — за штампом, як
        // у SQL; люди й літерали порожнього власника не дають.
        return (
          condition.field === OWNER_FIELD &&
          items.some(
            (item) =>
              isFilterToken(item) &&
              item.n === undefined &&
              UNIT_TOKENS.has(item.token) &&
              ownerInUnit(item.token, record, env.viewer),
          )
        );
      }
      const hit = items.some((item) => matchesItem(type, condition, stored, item, record, env));
      return op === 'not_in' ? !hit : hit;
    }
    case 'neq': {
      if (isFilterToken(condition.value) || Array.isArray(condition.value)) return false;
      const literal = coerceLiteral(type, condition.value);
      if (literal === undefined) return false;
      if (stored === null) return !exclude;
      return !sameValue(type, stored, literal);
    }
    case 'starts_with': {
      if (type !== 'text' && type !== 'picklist') return false;
      const prefix = condition.value;
      if (typeof prefix !== 'string' || prefix.length === 0 || prefix.length > 200) return false;
      return typeof stored === 'string' && normText(stored).startsWith(normText(prefix));
    }
    case 'lt':
    case 'lte':
    case 'gt':
    case 'gte': {
      let literal: unknown;
      if (isFilterToken(condition.value)) {
        // `now` — лише на моменті й лише з годинником.
        if (type !== 'datetime' || !isMomentToken(condition.value) || !env.clock) return false;
        literal = env.clock.now.getTime() * 1000;
      } else {
        literal = coerceLiteral(type, condition.value);
      }
      if (stored === null || literal === undefined) return false;
      const cmp = order(type, stored, literal);
      if (cmp === undefined) return false;
      if (op === 'lt') return cmp < 0;
      if (op === 'lte') return cmp <= 0;
      if (op === 'gt') return cmp > 0;
      return cmp >= 0;
    }
    case 'between': {
      const bounds = condition.value;
      if (!Array.isArray(bounds) || bounds.length !== 2) return false;
      const [a, b] = bounds.map((bound) =>
        isFilterToken(bound) ? undefined : coerceLiteral(type, bound),
      );
      if (a === undefined || b === undefined || stored === null) return false;
      const low = order(type, stored, a);
      const high = order(type, stored, b);
      return low !== undefined && high !== undefined && low >= 0 && high <= 0;
    }
    case 'on':
    case 'before':
    case 'after':
    case 'within':
      return dayCondition(type, op, stored, condition.value, env.clock);
    default:
      return false;
  }
}
